import React, { useCallback, useState } from 'react';
import {
  FieldCheckBox,
  FieldDatepicker,
  FieldInput,
  FieldNumberFormat,
  FormCol,
  FormCol1,
  FormCol2,
  FormCol4,
  FormCol6,
  FormRow,
} from 'motion-components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { Button, Col, Row } from 'react-bootstrap';
import './cnt.css';
import { FileRepoField } from 'shared-pages/components/filerepo-field';
import { ContributionStates, ContributorGroups } from 'shared-pages';
import { FaPen } from 'react-icons/fa';
import { promiseModalCntItemDetailsInput } from './modal-item-details-promise';
import { roundToDecimalPlaces } from './rounding';

/** Ενεργός τρόπος υπολογισμού: override από την τιμή περιόδου αν δεν είναι null */
function getPriceCalculationMethod(contributionItem) {
  const priceOverride = contributionItem?.unitPrice?.priceCalculationMethod;
  if (priceOverride != null) return priceOverride;
  return contributionItem?.item?.priceCalculationMethod;
}

/** Λεπτομέρειες (Ah κ.λπ.) μόνο όταν ο ενεργός τρόπος είναι 3 */
function usesItemDetails(contributionItem) {
  return (
    contributionItem?.item?.hasDetails && getPriceCalculationMethod(contributionItem) === 3
  );
}

function calculateContributionItemPrice(contributionItem) {
  const unitPrice = contributionItem.unitPrice.unitPrice;
  const priceCalculationMethod = getPriceCalculationMethod(contributionItem);
  const qty1 = parseFloat(contributionItem.qty1);
  const qty2 = parseFloat(contributionItem.qty2);
  let newPrice = !Number.isNaN(qty1) ? qty1 * unitPrice : undefined;

  if (priceCalculationMethod === 2) {
    newPrice = !Number.isNaN(qty2) ? unitPrice * qty2 : undefined;
  } else if (priceCalculationMethod === 3) {
    const qty3 = parseFloat(contributionItem.qty3);
    newPrice = !Number.isNaN(qty3) ? newPrice * qty3 : undefined;
  }

  return Number.isNaN(newPrice) || newPrice === undefined ? null : roundToDecimalPlaces(newPrice);
}

export const FormFields = ({ isNew, updateRecord, refetch, data, isFormEditable }) => {
  //
  const frmkCntx = useFormikContext();
  const { values } = frmkCntx;
  const { authStore, appStore } = useRootStore();

  const { handleGraphqlError } = useHandleError('PiBaseFormFields');
  const { t, i18n } = useTranslation();
  const gr = i18n.language === 'gr';
  function calcUserCanEdit() {
    const canEdit = appStore.isPlatformOwner || (isNew && appStore.isCollector);
    const canEditReasoning = '';
    return { canEdit, canEditReasoning };
  }
  const { canEdit, canEditReasoning } = calcUserCanEdit();

  return (
    <>
      {/* Header */}
      <FormRow>
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('Σειρά')}
          name={gr ? 'series.name' : 'series.enName'}
        />
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('Κατάσταση')}
          name="contributionstate.name"
        />
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('Υποβλήθηκε από')}
          name="submittedByUser"
        />
        <FieldDatepicker
          disabled={true}
          as={FormCol2}
          label={t('Υποβλήθηκε στις')}
          name="declarationDate"
        />
        <Col>
          <FieldCheckBox
            disabled={true}
            as={'div'}
            label={t('Είναι Ακυρωμένο')}
            name="isCanceled"
          />
        </Col>
      </FormRow>
      <FormRow>
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('Επωνυμία Υπόχρεου')}
          name="contributor.name"
          placeholder=""
        />
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('Περίοδος Αναφοράς')}
          // name="contributionperiod.months"
          name="periodyear"
          period={data?.year}
          placeholder=""
        />
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('Διεύθυνση')}
          name="contributor.addressLine1"
          placeholder=""
        />
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('ΑΦΜ')}
          name="contributor.vatNumber"
          placeholder=""
        />
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('Δ.Ο.Υ')}
          name="contributor.taxOffice"
          placeholder=""
        />
        <FieldInput
          disabled={true}
          as={FormCol2}
          label={t('Κατηγορία')}
          name={
            gr ? 'contributor.cntContributorsGroup.name' : 'contributor.cntContributorsGroup.enName'
          }
          placeholder=""
        />
      </FormRow>

      {/* Δήλωση Εμπορικών Σημάτων */}
      <h4 className="mt-3">{t('Κατάλογος εμπορικών σημάτων')}: </h4>
      <p>
        {gr
          ? `
        Η συμπλήρωση του καταλόγου εμπορικών σημάτων που διακινεί η εταιρεία σας είναι υποχρεωτική,
        έστω και αν είναι επιμέρους κοινός με άλλων επιχειρήσεων (εισάγεται κοινά σήματα). Επίσης
        είναι υποχρεωτική η ενημέρωση του συστήματος σε περίπτωση μεταβολών του καταλόγου.
        `
          : `
        Completing the list of trademarks traded by your company is mandatory,
        even if it is partially shared with other businesses (common trademarks are entered).
        It is also mandatory to update the system in case of changes to the list.`}
      </p>
      <FormRow>
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="1"
          name="userStringField1"
          placeholder=""
        />
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="2"
          name="userStringField2"
          placeholder=""
        />
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="3"
          name="userStringField3"
          placeholder=""
        />
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="4"
          name="userStringField4"
          placeholder=""
        />
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="5"
          name="userStringField5"
          placeholder=""
        />
      </FormRow>
      <FormRow>
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="6"
          name="userStringField6"
          placeholder=""
        />
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="7"
          name="userStringField7"
          placeholder=""
        />
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="8"
          name="userStringField8"
          placeholder=""
        />
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="9"
          name="userStringField9"
          placeholder=""
        />
        <FieldInput
          disabled={!isFormEditable}
          as={FormCol2}
          label="10"
          name="userStringField10"
          placeholder=""
        />
      </FormRow>

      {/* Lines -------------------------------------------------- */}
      <FormLineFields
        useInNonEdoe={data?.contributor?.contributorGroupId === ContributorGroups.Plain}
        useInEdoe={data?.contributor?.contributorGroupId === ContributorGroups.Edoe}
        useInEdoeUsed={data?.contributor?.contributorGroupId === ContributorGroups.EdoeUsed}
        isFormEditable={isFormEditable}
      />

      {/* Files -------------------------------------------------- */}
      {/* πεδία πλατφόρμας για τιμολόγηση εμφάνισε για τις οριστικοποιημενες*/}
      {data?.statusId === ContributionStates.Saved && (
        <div className="border cnt-row-invoice mt-4 pt-2 pl-3">
          <FormRow>
            {appStore.isPlatformOwner ? (
              <FormCol2>
                <FieldCheckBox
                  // disabled={data?.releaseToErp === true}
                  disabled={true}
                  as={FormCol}
                  label={t('Διαθέσιμο στο ERP')}
                  name="releaseToErp"
                />
                {data.erpEntityCode && <div className="mb-2">{data.erpEntityCode}</div>}
              </FormCol2>
            ) : (
              <FormCol2></FormCol2>
            )}

            {appStore.isPlatformOwner ? (
              <FormCol2>
                <FieldCheckBox
                  disabled={!appStore.isPlatformOwner}
                  as={'div'}
                  label={t('Τιμολογημένο')}
                  name="isInvoiced"
                />
              </FormCol2>
            ) : (
              <FormCol2></FormCol2>
            )}

            <FileRepoField
              as={FormCol6}
              label={t('Τιμολόγιο')}
              fileIdFieldName="invoiceFileId"
              fileRec={data?.invoiceFile}
              canEdit={appStore.isPlatformOwner}
            />
          </FormRow>
          {/* Πιστωτικό Τιμολόγιο */}
          {data?.isCanceled && (
            <FormRow>
              <FormCol4></FormCol4>
              <FileRepoField
                as={FormCol6}
                label={t('Πιστωτικό Τιμολόγιο')}
                fileIdFieldName="creditInvoiceFileId"
                fileRec={data?.creditInvoiceFile}
                canEdit={appStore.isPlatformOwner}
              />
            </FormRow>
          )}
        </div>
      )}
    </>
  );
};

/* --------------------------------------------------------------------------------------------- */
/* Lines                                                                                         */
/* --------------------------------------------------------------------------------------------- */
export const FormLineFields = ({ useInNonEdoe, useInEdoe, useInEdoeUsed, isFormEditable }) => {
  //
  const frmkCntx = useFormikContext();
  const { values, setFieldValue } = frmkCntx;
  const { t, i18n } = useTranslation();
  const gr = i18n.language === 'gr';

  // Υπολογισμός Calculated Fields
  const [idxTouched, setIdxTouched] = useState(-1);
  const [recalculateLoadedValues, setRecalculateLoadedValues] = useState(true);
  const handleQtyChange = useCallback(
    (fieldName, idx, value) => {
      // Τα values του formik δεν έχουν τιμές εδώ και
      // για αυτό φτιάχτηκε ξεχωριστό effect
      setIdxTouched(idx);
    },
    [setIdxTouched],
  );

  React.useEffect(() => {
    setRecalculateLoadedValues(true);
  }, [(values as any)?.id]);

  React.useEffect(() => {
    if (!(values && (values as any)?.contributionItems)) return;
    if (!recalculateLoadedValues || idxTouched >= 0) return;

    const idxToRecalculate = (values as any).contributionItems.findIndex((contributionItem) => {
      const calculatedPrice = calculateContributionItemPrice(contributionItem);
      const currentPrice =
        contributionItem.price === null || contributionItem.price === undefined
          ? null
          : Number(contributionItem.price);

      return calculatedPrice !== currentPrice;
    });

    if (idxToRecalculate >= 0) setIdxTouched(idxToRecalculate);
    else setRecalculateLoadedValues(false);
  }, [values, idxTouched, recalculateLoadedValues]);

  React.useEffect(() => {
    /**
     * Υπολογισμός αξίας γραμμής και αθροισμάτων ανα Πίνακα και Συνολικά
     */
    if (!(values && (values as any)?.contributionItems)) return;
    if (idxTouched < 0) return; // Τερματισμός Υπολογισμών
    setIdxTouched(-1); // Τερματισμός Υπολογισμών

    const lcValues = {
      ...(values as any),
      contributionItems: [...(values as any).contributionItems],
    }; // local copy for sums and update
    /**
     * Άλλαξε κάποια τιμή στην γραμμή => υπολόγισε Αξία γραμμής
     */
    const unitPrice = (values as any).contributionItems[idxTouched].unitPrice.unitPrice;
    const priceCalculationMethod = getPriceCalculationMethod(
      (values as any).contributionItems[idxTouched],
    );
    const qty1 = parseFloat((values as any).contributionItems[idxTouched].qty1);
    const qty2 = parseFloat((values as any).contributionItems[idxTouched].qty2);
    const curPrice = (values as any).contributionItems[idxTouched].price;
    let newPrice = !Number.isNaN(qty1) ? qty1 * unitPrice : undefined;
    if (priceCalculationMethod === 2) {
      newPrice = !Number.isNaN(qty2) ? unitPrice * qty2 : undefined;
    } else if (priceCalculationMethod === 3) {
      const qty3 = parseFloat((values as any).contributionItems[idxTouched].qty3);
      newPrice = !Number.isNaN(qty3) ? newPrice * qty3 : undefined;
    }

    // Ενημέρωσε νέα Αξία αν είναι διαφορετική (σε 2 δεκαδικά)
    if (newPrice !== curPrice) {
      if (Number.isNaN(newPrice) || newPrice === undefined)
        setFieldValue(`contributionItems[${idxTouched}].price`, null);
      else {
        newPrice = roundToDecimalPlaces(newPrice);
        setFieldValue(`contributionItems[${idxTouched}].price`, newPrice);
      }
    }
    lcValues.contributionItems[idxTouched] = {
      ...lcValues.contributionItems[idxTouched],
      price: newPrice,
    }; // update local copy

    /**
     * Αθροίσματα (Μερικά) ανα Πίνακα
     */
    const groupId = (values as any).contributionItems[idxTouched].item.groupId;
    const newSubTotal = (lcValues as any).contributionItems.reduce(
      (acc, el) => {
        if (el.item.groupId === groupId) {
          return {
            qty1: acc.qty1 + (el.qty1 ? el.qty1 : 0),
            qty2: acc.qty2 + (el.qty2 ? el.qty2 : 0),
            price: acc.price + (el.price ? el.price : 0),
          };
        } else return acc;
      },
      { qty1: 0, qty2: 0, price: 0 },
    );

    const curSubTotalsGroup =
      (lcValues as any).subTotals && (lcValues as any).subTotals[`group${groupId}`]
        ? (lcValues as any).subTotals[`group${groupId}`]
        : undefined;
    // Ενημέρωσε τιμές αθροισμάτων στο formik
    if (newSubTotal.qty1 !== curSubTotalsGroup?.qty1)
      setFieldValue(`subTotals.group${groupId}.qty1`, newSubTotal.qty1);
    if (newSubTotal.qty2 !== curSubTotalsGroup?.qty2)
      setFieldValue(`subTotals.group${groupId}.qty2`, newSubTotal.qty2);
    if (newSubTotal.price !== curSubTotalsGroup?.price)
      setFieldValue(`subTotals.group${groupId}.price`, newSubTotal.price);

    if (!lcValues.subTotals) lcValues.subTotals = {}; // Ενημέρωσε local copy
    lcValues.subTotals[`group${groupId}`] = newSubTotal;

    /**
     * Συνολικό Άθροισμα
     */
    let newTotalCost = 0;
    let newTotalQty = 0;
    let newTotalWeight = 0;
    for (const [key, el] of Object.entries((lcValues as any).subTotals)) {
      newTotalCost = newTotalCost + ((el as any).price ? (el as any).price : 0);
      newTotalQty = newTotalQty + ((el as any).qty1 ? (el as any).qty1 : 0);
      newTotalWeight = newTotalWeight + ((el as any).qty2 ? (el as any).qty2 : 0);
    }
    // Ενημέρωσε τιμές αθροισμάτων στο formik
    setFieldValue(`totalCost`, newTotalCost); // header record
    setFieldValue(`totalQty`, newTotalQty); // header record
    setFieldValue(`totalWeight`, newTotalWeight); // header record
  }, [values, idxTouched]);

  /**
   *  Render
   */
  if (!(values as any).contributionItems)
    return <div key={`div`}>{t('Λάθος! Δεν βρέθηκαν εισφορές')}</div>;

  const visibleContributionItems = (values as any).contributionItems
    .map((el, idx, arr) => {
      return { ...el, baseIdx: idx };
    })
    .filter((el, idx, arr) => {
      let showLine = false;
      if (useInNonEdoe && el.item.useInNonEdoe) showLine = true;
      else if (useInEdoe && el.item.useInEdoe) showLine = true;
      else if (useInEdoeUsed && el.item.useInEdoeUsed) showLine = true;
      return showLine;
    });
  // .filter((el, idx, arr) => {
  //   return idx === 17;
  // }); //$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$
  return (
    <div key={`div`}>
      {visibleContributionItems
        .filter((el, idx, arr) => {
          let showLine = false;
          if (useInNonEdoe && el.item.useInNonEdoe) showLine = true;
          else if (useInEdoe && el.item.useInEdoe) showLine = true;
          else if (useInEdoeUsed && el.item.useInEdoeUsed) showLine = true;
          return showLine;
        })
        .map((el, fIdx, arr) => {
          const item = el.item;
          // Θέσε τίτλο group & subgroup αν αλλάξει τιμή
          const group =
            (fIdx === 0 ? undefined : arr[fIdx - 1].item.group?.name) !== item.group?.name
              ? gr
                ? item.group?.name
                : item.group?.enName
              : undefined;
          const subgroup =
            (fIdx === 0 ? undefined : arr[fIdx - 1].item.subgroup?.name) !== item.subgroup?.name
              ? gr
                ? item.subgroup?.name
                : item.subgroup?.enName
              : undefined;
          const footnote =
            fIdx === 0 ? undefined : gr ? arr[fIdx].item.footnote : arr[fIdx].item.enFootnote;
          // for subTotals
          const isLastGroupRow =
            fIdx === arr.length - 1 || arr[fIdx + 1].item.group?.name !== item.group?.name;
          const groupId = arr[fIdx].item.groupId;

          // Τιμή Μονάδας
          let unitPriceLabel = '';
          const priceCalculationMethod = getPriceCalculationMethod(arr[fIdx]);
          const hasDetails = usesItemDetails(arr[fIdx]);
          if (priceCalculationMethod === 1) {
            unitPriceLabel = t('Εισφορά ανά Τεμάχιο');
          } else if (priceCalculationMethod === 2) {
            unitPriceLabel = t('Εισφορά ανά Κιλό');
          } else if (priceCalculationMethod === 3) {
            unitPriceLabel = t('Εισφορά ανά Τεμάχιο');
          }

          const idx = el.baseIdx;

          /*  ************************
              Πινακάκι με Detail Τιμές
          */
          async function showItemDetails(idx: any) {
            //

            const formValues = await promiseModalCntItemDetailsInput({
              title: `${t('Δήλωση Επιμέρους Στοιχείων')}: (${el.item.code}) ${
                gr ? el.item.name : el.item.enName
              }`,
              data: el,
              itemIdx: idx,
              contributionItemId: el.id,
              isFormEditable,
            });
            if (isFormEditable) {
              if (formValues.qty1 === 0) formValues.qty1 = null;
              if (formValues.qty2 === 0) formValues.qty2 = null;
              if (formValues.qty3 === 0 || isNaN(formValues.qty3)) formValues.qty3 = null;
              if (formValues.price === 0) formValues.price = null;
              setFieldValue(`contributionItems[${idx}]`, formValues);
              // Force Recalc Sums
              setIdxTouched(idx);
            }
          }

          // Render Line
          return (
            <div key={`div-${idx}`}>
              {/* 1η Επικεφαλίδα (Πίνακα) */}
              {group && (
                <h4 className="form-group-title mt-5" key={`h4-${idx}`}>
                  {group}
                </h4>
              )}
              {/* 2η Επικεφαλίδα */}
              {subgroup && (
                <h5 className="mt-2" key={`h5-${idx}`}>
                  {subgroup}
                </h5>
              )}

              <FormRow key={idx + 1} className="cnt-row">
                {/* {idx + 1} {item?.code} {item?.name} */}
                <FieldInput
                  key={`codeitem${idx}`}
                  disabled={true}
                  as={FormCol1}
                  label={t('Κωδικός')}
                  name={`contributionItems[${idx}].item.code`}
                  placeholder=""
                />
                <FieldInput
                  key={`nameitem${idx}`}
                  disabled={true}
                  as={FormCol4}
                  label={t('Είδος')}
                  name={
                    gr
                      ? `contributionItems[${idx}].item.name`
                      : `contributionItems[${idx}].item.enName`
                  }
                  placeholder=""
                />

                {/* <FieldInput
                    key={`qty1_${idx}`}
                    disabled={!isFormEditable}
                    as={FormCol2}
                    label={t(el?.item.units1)}
                    name={`contributionItems[${idx}].qty1`}
                    placeholder=""
                    onChange={(e) => handleQtyChange('qty1', idx, e.target.value)}
                  /> */}
                {/* Ποσότητα 1 */}
                {!hasDetails && (
                  <FieldNumberFormat
                    key={`qty1_${idx}`}
                    disabled={!isFormEditable}
                    as={FormCol2}
                    label={t(el?.item.units1)}
                    name={`contributionItems[${idx}].qty1`}
                    decimalScale={0}
                    placeholder=""
                    onChange={(e) => handleQtyChange('qty1', idx, e.target.value)}
                  />
                )}
                {hasDetails && (
                  <Col sm={8} lg={3} xl={3} className="m-0 p-0">
                    <Row className="m-0 p-0 flex-nowrap align-items-end g-1">
                      <FieldNumberFormat
                        key={`qty1_${idx}`}
                        disabled={true}
                        as={Col}
                        className="px-1"
                        label={t(el?.item.units1)}
                        name={`contributionItems[${idx}].qty1`}
                        decimalScale={0}
                        placeholder=""
                      />
                      <FieldNumberFormat
                        key={`qty3_${idx}`}
                        disabled={true}
                        as={Col}
                        className="px-1"
                        label={t(el?.item.units3)}
                        name={`contributionItems[${idx}].qty3`}
                        placeholder=""
                      />
                      <Col className="col-auto flex-shrink-0 px-0 pb-2">
                        <Button
                          variant="light"
                          className="btn-circle-edit"
                          onClick={() => {
                            showItemDetails(idx);
                          }}
                          name="edit"
                          data-toggle="tooltip"
                          data-placement="left"
                          title={t('Επεξεργασία Εγγραφής')}
                        >
                          <FaPen color="#000" />
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                )}
                {/* Ποσότητα 2 */}
                <FieldNumberFormat
                  key={`qty2_${idx}`}
                  disabled={!isFormEditable || hasDetails}
                  as={FormCol2}
                  label={t(el?.item.units2)}
                  name={`contributionItems[${idx}].qty2`}
                  placeholder=""
                  onChange={(e) => {
                    handleQtyChange('qty2', idx, e.target.value);
                  }}
                />
                <FieldNumberFormat
                  key={`unitprice_${idx}`}
                  decimalScale={3}
                  disabled={true}
                  as={FormCol1}
                  // label={t('Τιμή Μονάδας')}
                  label={unitPriceLabel}
                  name={`contributionItems[${idx}].unitPrice.unitPrice`}
                  placeholder=""
                  suffixLabel="€"
                />
                <FieldNumberFormat
                  key={`price_${idx}`}
                  disabled={true}
                  as={FormCol2}
                  label={t('Αξία')}
                  name={`contributionItems[${idx}].price`}
                  placeholder=""
                  suffixLabel="€"
                />
              </FormRow>
              {/* Υποσημείωση */}
              {footnote && (
                <h6 className="mb-3" key={`h6-${idx}`}>
                  {footnote}
                </h6>
              )}
              {/* Αθροίσματα Ενότητας */}
              {isLastGroupRow && (
                <FormRow className="cnt-row pt-4" key={`sum-${idx}`}>
                  <FormCol2 className="mb-2" style={{ alignSelf: 'center' }} key={`subsum-${idx}`}>
                    <strong>{t('Μερικό Άθροισμα')}</strong>
                  </FormCol2>
                  <FormCol1></FormCol1>
                  <FormCol1></FormCol1>
                  <FormCol1></FormCol1>

                  <FieldNumberFormat
                    key={`subTotals_group${groupId}_Qty1`}
                    disabled={true}
                    as={FormCol2}
                    label=""
                    name={`subTotals.group${groupId}.qty1`}
                    placeholder=""
                    suffixLabel={t('ΤΜΧ')}
                    suffixWidth="64"
                  />

                  <FieldNumberFormat
                    key={`subTotals_group${groupId}_Qty2`}
                    disabled={true}
                    as={FormCol2}
                    label=""
                    name={`subTotals.group${groupId}.qty2`}
                    placeholder=""
                    suffixLabel={t('kg')}
                  />
                  <FormCol1 key={`sum-emptycol-${idx}`}></FormCol1>
                  <FieldNumberFormat
                    key={`subTotals_group${groupId}_Price`}
                    disabled={true}
                    as={FormCol2}
                    label=""
                    name={`subTotals.group${groupId}.price`}
                    placeholder=""
                    suffixLabel="€"
                  />
                </FormRow>
              )}
            </div>
          );
        })}

      {/* Αθροίσματα*/}
      <FormRow className="cnt-row-totals pt-4">
        <FormCol2 className="mb-2" style={{ alignSelf: 'center' }}>
          <strong>{t('Συνολικό Άθροισμα')}</strong>
        </FormCol2>
        <FormCol1></FormCol1>
        <FormCol1></FormCol1>
        <FormCol1></FormCol1>

        <FieldNumberFormat
          key={`totalQty`}
          disabled={true}
          as={FormCol2}
          label=""
          name={`totalQty`}
          placeholder=""
          suffixLabel={t('ΤΜΧ')}
          suffixWidth="64"
        />

        <FieldNumberFormat
          key={`totalWeight`}
          disabled={true}
          as={FormCol2}
          label=""
          name={`totalWeight`}
          placeholder=""
          suffixLabel={t('kg')}
        />
        <FormCol1 />
        <FieldNumberFormat
          key={`totalCost`}
          disabled={true}
          as={FormCol2}
          label=""
          name={'totalCost'}
          placeholder=""
          suffixLabel="€"
        />
      </FormRow>
      <FormRow className="cnt-row-totals pt-4">
        <FormCol4 className="mb-2" style={{ alignSelf: 'center' }}>
          <strong>{t('Συνολική Χρηματική Εισφορά σε ευρώ (συμπεριλαμβανομένου ΦΠΑ)')}</strong>
        </FormCol4>
        <FormCol4></FormCol4>
        <FormCol2></FormCol2>
        <FieldNumberFormat
          key={`totalCost`}
          disabled={true}
          as={FormCol2}
          label=""
          name={'totalCostWithVat'}
          placeholder=""
          suffixLabel="€"
        />
      </FormRow>
    </div>
  );
};
